import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { ArchiveTray, ArrowsClockwise, Broadcast, ChatCircle, MagnifyingGlass, Pause, Play } from "@phosphor-icons/react";
import { Bar, BarChart, CartesianGrid, Cell, LabelList, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useTheme } from "styled-components";

import { Avatar } from "../../../components/Avatar";
import { Badge } from "../../../components/Badge";
import { Button, Size } from "../../../components/Buttons";
import { IconButton, Shape } from "../../../components/Buttons/IconButton";
import { PageTitle } from "../../../components/PageTitle";
import { useApi } from "../../../providers/api/useApiProvider";
import { useStore } from "../../../store";
import { Message, MessageType } from "../../../models/Message";
import { Service, ServiceStatus } from "../../../models/Service";
import { UserRole } from "../../../models/User";
import { DashboardDailyQueueServiceTotal, DashboardData, getDashboard } from "../../../services/dashboardServices";
import { getServices, Order } from "../../../services/serviceServices";
import { getAssignedRole, hasRole, roleGroups } from "../../../services/accessControl";
import { ChartFrame, DashboardHeader, DashboardHalfSections, DashboardTabButton, DashboardTabs, DashboardView, EmptyState, FilterBar, MetricCard, MetricsGrid, NowCardBody, NowCardFooter, NowCardHeader, NowCardsGrid, NowControls, NowGroupCard, NowServiceRow, NowStatus, NowSummaryGrid, NowTabButton, NowTabs, Panel, RankingTable } from "./styles";

const today = new Date();
const defaultTo = formatDateInput( today );
const defaultFrom = formatDateInput( new Date( today.getFullYear(), today.getMonth(), today.getDate() - 30 ));

const statusLabels: Record<string, string> = {
  AUTOMATED: "Automatizado",
  WAITING: "Aguardando",
  PROGRESS: "Em atendimento",
  FINALIZED: "Finalizado",
};

type DailyQueueChartItem = {
  date: string;
  label: string;
  [key: string]: string | number;
};

type DashboardTab = "now" | "analysis";
type NowTab = "users" | "queues" | "summary";
type NowGroupMode = "users" | "queues" | "channels";

type CurrentService = {
  service: Service;
};

type NowCard = {
  id: string;
  title: string;
  subtitle?: string;
  status?: "online" | "offline" | "neutral";
  avatarName?: string;
  avatarUrl?: string | null;
  services: Array<CurrentService>;
};

export const Dashboard = () => {
  const theme = useTheme();
  const { simpleApiCall } = useApi();
  const simpleApiCallRef = useRef(simpleApiCall);
  const { user } = useStore();
  const assignedRole = getAssignedRole( user );
  const [ activeDashboardTab, setActiveDashboardTab ] = useState<DashboardTab>("now");
  const [ activeNowTab, setActiveNowTab ] = useState<NowTab>("users");
  const [ from, setFrom ] = useState( defaultFrom );
  const [ to, setTo ] = useState( defaultTo );
  const [ appliedFrom, setAppliedFrom ] = useState( defaultFrom );
  const [ appliedTo, setAppliedTo ] = useState( defaultTo );
  const [ dashboard, setDashboard ] = useState<DashboardData | null>( null );
  const [ currentServices, setCurrentServices ] = useState<Array<CurrentService>>([]);
  const [ nowRefreshVersion, setNowRefreshVersion ] = useState( 0 );
  const [ nowAutoRefresh, setNowAutoRefresh ] = useState( false );
  const [ nowLoading, setNowLoading ] = useState( false );
  const [ nowError, setNowError ] = useState( "" );
  const [ nowUpdatedAt, setNowUpdatedAt ] = useState<Date | null>( null );

  simpleApiCallRef.current = simpleApiCall;

  useEffect(() => {
    if( activeDashboardTab !== "analysis" ) return;

    let mounted = true;

    simpleApiCallRef.current( getDashboard, { from: appliedFrom, to: appliedTo }, undefined, { showSuccessMessage: false } )
      .then(( result ) => {
        if( mounted ) setDashboard( result );
      });

    return () => {
      mounted = false;
    };
  }, [ activeDashboardTab, appliedFrom, appliedTo ]);

  useEffect(() => {
    if( !user || !hasRole( assignedRole, roleGroups.dashboard )) return;

    let mounted = true;

    setNowLoading( true );
    setNowError( "" );
    loadCurrentServices()
      .then(( services ) => {
        if( !mounted ) return;
        setCurrentServices( services );
        setNowUpdatedAt( new Date());
      })
      .catch(( error ) => {
        if( !mounted ) return;
        setCurrentServices([]);
        setNowError( error?.message ?? "Falha ao carregar atendimentos em andamento" );
      })
      .finally(() => {
        if( mounted ) setNowLoading( false );
      });

    return () => {
      mounted = false;
    };
  }, [ nowRefreshVersion, user, assignedRole ]);

  useEffect(() => {
    if( !nowAutoRefresh ) return;

    const interval = window.setInterval(() => {
      setNowRefreshVersion(( value ) => value + 1 );
    }, 30000 );

    return () => window.clearInterval( interval );
  }, [ nowAutoRefresh ]);

  const maxStatus = useMemo(() => Math.max( 1, ...(( dashboard?.servicesByStatus ?? [] ).map(( item ) => item.total ))), [ dashboard ]);

  const showServiceQueueYear = useMemo(() => {
    const years = new Set(( dashboard?.servicesByQueueDay ?? [] ).map(( item ) => getChartDateYear( item.date )));
    years.delete( "" );
    return years.size > 1;
  }, [ dashboard ]);

  const serviceQueueSeries = useMemo(() => {
    const series = new Map<string, { key: string; name: string; total: number }>();

    for( const item of dashboard?.servicesByQueueDay ?? [] ) {
      const key = getServiceQueueKey( item );
      const current = series.get( key );
      const name = item.organizationName ? `${ item.queueName } - ${ item.organizationName }` : item.queueName;

      series.set( key, {
        key,
        name,
        total: ( current?.total ?? 0 ) + item.total,
      });
    }

    return Array.from( series.values()).sort(( first, second ) => second.total - first.total || first.name.localeCompare( second.name ));
  }, [ dashboard ]);

  const serviceQueueChartData = useMemo(() => {
    const days = new Map<string, DailyQueueChartItem>();

    for( const item of dashboard?.servicesByQueueDay ?? [] ) {
      const key = getServiceQueueKey( item );
      const day = days.get( item.date ) ?? {
        date: item.date,
        label: formatChartDate( item.date, showServiceQueueYear ),
      };
      const current = typeof day[ key ] === "number" ? day[ key ] as number : 0;

      day[ key ] = current + item.total;
      days.set( item.date, day );
    }

    return Array.from( days.values()).sort(( first, second ) => first.date.localeCompare( second.date ));
  }, [ dashboard, showServiceQueueYear ]);

  const dailyLabelStep = useMemo(() => Math.max( 1, Math.ceil( serviceQueueChartData.length / 8 )), [ serviceQueueChartData.length ]);

  const serviceQueueTicks = useMemo(() => serviceQueueChartData
    .filter(( _, index, list ) => shouldShowDailyLabel( index, list.length, dailyLabelStep ))
    .map(( item ) => item.label), [ dailyLabelStep, serviceQueueChartData ]);

  const serviceQueueColors = useMemo(() => [
    theme.colors.info.default,
    theme.colors.success.default,
    theme.colors.warning.default,
    theme.colors.error.default,
    theme.colors.secondary.default,
    theme.colors.primary.default,
  ], [ theme ]);

  const statusChartData = useMemo(() => ( dashboard?.servicesByStatus ?? [] ).map(( item ) => ({
    label: statusLabels[ item.status ] ?? item.status,
    total: item.total,
  })), [ dashboard ]);

  const queueChartData = useMemo(() => ( dashboard?.queues ?? [] ).slice( 0, 10 ).map(( queue ) => ({
    label: queue.name,
    organization: queue.organizationName,
    total: queue.services,
  })), [ dashboard ]);

  const ratingChartData = useMemo(() => {
    const completed = dashboard?.ratings.completed ?? 0;
    const declined = dashboard?.ratings.declined ?? 0;
    const successRate = dashboard?.ratings.successRate ?? 0;
    const successful = Math.round(( completed * successRate ) / 100 );
    const unsuccessful = Math.max( 0, completed - successful );

    return [
      { label: "Sucesso", total: successful, color: theme.colors.success.default },
      { label: "Sem sucesso", total: unsuccessful, color: theme.colors.warning.default },
      { label: "Recusadas", total: declined, color: theme.colors.error.default },
    ].filter(( item ) => item.total > 0 );
  }, [ dashboard, theme ]);

  const userCards = useMemo(() => buildNowCards( currentServices, "users" ), [ currentServices ]);
  const queueCards = useMemo(() => buildNowCards( currentServices, "queues" ), [ currentServices ]);
  const channelCards = useMemo(() => buildNowCards( currentServices, "channels" ), [ currentServices ]);

  const nowSummary = useMemo(() => ({
    services: currentServices.length,
    users: userCards.filter(( card ) => card.id !== "without-user" ).length,
    queues: queueCards.filter(( card ) => card.id !== "without-queue" ).length,
    channels: channelCards.filter(( card ) => card.id !== "without-channel" ).length,
  }), [ channelCards, currentServices.length, queueCards, userCards ]);

  const roleTitle = assignedRole === UserRole.ADMIN
    ? "Visão geral da organização"
    : "Visão das suas filas";

  const applyDashboardFilters = () => {
    setAppliedFrom( from );
    setAppliedTo( to );
  };

  const handleSearch = ( event: FormEvent<HTMLFormElement> ) => {
    event.preventDefault();
    applyDashboardFilters();
  };

  const refreshNow = () => {
    setNowRefreshVersion(( value ) => value + 1 );
  };

  return (
    <DashboardView>
      <DashboardHeader>
        <div>
          <PageTitle className="dashboard-header-title" as="h1">{ activeDashboardTab === "now" ? "Agora" : "Dashboard" }</PageTitle>
          <span className="dashboard-header-subtitle">{ activeDashboardTab === "now" ? "Atendimentos em andamento" : roleTitle }</span>
        </div>

        {
          activeDashboardTab === "analysis"
            ? (
                <FilterBar onSubmit={ handleSearch }>
                  <label className="filter-field">
                    De
                    <input
                      className="filter-input"
                      type="date"
                      value={ from }
                      onChange={( event ) => setFrom( event.target.value )}
                    />
                  </label>

                  <label className="filter-field">
                    Até
                    <input
                      className="filter-input"
                      type="date"
                      value={ to }
                      onChange={( event ) => setTo( event.target.value )}
                    />
                  </label>

                  <IconButton
                    color="info"
                    onClick={ applyDashboardFilters }
                    title="Buscar dashboard"
                    aria-label="Buscar dashboard"
                  >
                    <MagnifyingGlass size={ 18 } />
                  </IconButton>
                </FilterBar>
              )
            : (
                <NowControls>
                  <Button
                    $color="info"
                    $size={ Size.SMALL }
                    onClick={ refreshNow }
                    disabled={ nowLoading }
                  >
                    <ArrowsClockwise size={ 18 } />
                    Atualizar
                  </Button>
                  <IconButton
                    color="info"
                    shape={ Shape.CIRCLE }
                    onClick={() => setNowAutoRefresh(( value ) => !value )}
                    title={ nowAutoRefresh ? "Pausar atualização automática" : "Iniciar atualização automática" }
                    aria-label={ nowAutoRefresh ? "Pausar atualização automática" : "Iniciar atualização automática" }
                  >
                    { nowAutoRefresh ? <Pause size={ 18 } /> : <Play size={ 18 } /> }
                  </IconButton>
                </NowControls>
              )
        }
      </DashboardHeader>

      <DashboardTabs>
        <DashboardTabButton
          className="dashboard-tab-button"
          $active={ activeDashboardTab === "now" }
          onClick={() => setActiveDashboardTab( "now" )}
        >
          Agora
        </DashboardTabButton>
        <DashboardTabButton
          className="dashboard-tab-button"
          $active={ activeDashboardTab === "analysis" }
          onClick={() => setActiveDashboardTab( "analysis" )}
        >
          Analise
        </DashboardTabButton>
      </DashboardTabs>

      {
        activeDashboardTab === "now"
          ? (
              <>
                <NowTabs>
                  <NowTabButton
                    className="now-tab-button"
                    $active={ activeNowTab === "users" }
                    onClick={() => setActiveNowTab( "users" )}
                  >
                    <ChatCircle size={ 20 } />
                    Em atendimento
                  </NowTabButton>
                  <NowTabButton
                    className="now-tab-button"
                    $active={ activeNowTab === "queues" }
                    onClick={() => setActiveNowTab( "queues" )}
                  >
                    <ArchiveTray size={ 20 } />
                    Filas de atendimento
                  </NowTabButton>
                  <NowTabButton
                    className="now-tab-button"
                    $active={ activeNowTab === "summary" }
                    onClick={() => setActiveNowTab( "summary" )}
                  >
                    <Broadcast size={ 20 } />
                    Resumo
                  </NowTabButton>
                </NowTabs>

                { nowUpdatedAt ? <NowStatus>Atualizado às { formatTime( nowUpdatedAt )}</NowStatus> : null }
                { nowLoading ? <EmptyState>Carregando atendimentos em andamento...</EmptyState> : null }
                { nowError ? <EmptyState>{ nowError }</EmptyState> : null }

                {
                  !nowLoading && !nowError && activeNowTab === "users"
                    ? renderNowCards( userCards, "Nenhum atendimento em andamento por atendente.", "user" )
                    : null
                }

                {
                  !nowLoading && !nowError && activeNowTab === "queues"
                    ? renderNowCards( queueCards, "Nenhum atendimento em andamento por fila.", "queue" )
                    : null
                }

                {
                  !nowLoading && !nowError && activeNowTab === "summary"
                    ? (
                        <>
                          <NowSummaryGrid>
                            <MetricCard>
                              <span>Em andamento</span>
                              <strong>{ nowSummary.services }</strong>
                            </MetricCard>
                            <MetricCard>
                              <span>Atendentes</span>
                              <strong>{ nowSummary.users }</strong>
                            </MetricCard>
                            <MetricCard>
                              <span>Filas</span>
                              <strong>{ nowSummary.queues }</strong>
                            </MetricCard>
                            <MetricCard>
                              <span>Canais</span>
                              <strong>{ nowSummary.channels }</strong>
                            </MetricCard>
                          </NowSummaryGrid>

                          { renderNowCards( channelCards, "Nenhum atendimento em andamento por canal.", "channel" ) }
                        </>
                      )
                    : null
                }
              </>
            )
          : (
              <>

      <MetricsGrid>
        <MetricCard>
          <span>Atendimentos</span>
          <strong>{ dashboard?.summary.services ?? 0 }</strong>
        </MetricCard>

        <MetricCard>
          <span>Mensagens</span>
          <strong>{ dashboard?.summary.messages ?? 0 }</strong>
        </MetricCard>

        <MetricCard>
          <span>TME</span>
          <strong>{ formatDuration( dashboard?.summary.averageWaitingSeconds ?? 0 )}</strong>
        </MetricCard>

        <MetricCard>
          <span>TMA</span>
          <strong>{ formatDuration( dashboard?.summary.averageHandlingSeconds ?? 0 )}</strong>
        </MetricCard>

        <MetricCard>
          <span>Atendentes</span>
          <strong>{ dashboard?.summary.attendants ?? 0 }</strong>
        </MetricCard>

        <MetricCard>
          <span>Avaliação média</span>
          <strong>{ formatScore( dashboard?.ratings.averageScore )}</strong>
        </MetricCard>
      </MetricsGrid>

      <Panel>
        <h2>Atendimentos por dia</h2>
        {
          serviceQueueChartData.length > 0
            ? (
                <ChartFrame $height={ 280 }>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={ serviceQueueChartData } margin={{ top: 8, right: 12, left: -20, bottom: 0 }}>
                      <CartesianGrid stroke={ `${ theme.colors.text.default }18` } vertical={ false } />
                      <XAxis dataKey="label" ticks={ serviceQueueTicks } stroke={ `${ theme.colors.text.default }99` } fontSize={ 11 } />
                      <YAxis stroke={ `${ theme.colors.text.default }99` } fontSize={ 11 } allowDecimals={ false } />
                      <Tooltip cursor={{ fill: `${ theme.colors.text.default }0D` }} />
                      <Legend />
                      { serviceQueueSeries.map(( series, index ) => (
                        <Bar
                          key={ series.key }
                          dataKey={ series.key }
                          name={ series.name }
                          stackId="services"
                          fill={ serviceQueueColors[ index % serviceQueueColors.length ] }
                          radius={[ 4, 4, 0, 0 ]}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </ChartFrame>
              )
            : <EmptyState>Nenhum atendimento com fila no período.</EmptyState>
        }
      </Panel>

      <DashboardHalfSections>
        <Panel>
          <h2>Status dos atendimentos</h2>
          {
            statusChartData.length > 0
              ? (
                  <ChartFrame $height={ 220 }>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={ statusChartData } layout="vertical" margin={{ top: 8, right: 40, left: 12, bottom: 0 }}>
                        <CartesianGrid stroke={ `${ theme.colors.text.default }18` } horizontal={ false } />
                        <XAxis type="number" domain={[ 0, maxStatus ]} hide allowDecimals={ false } />
                        <YAxis type="category" dataKey="label" width={ 112 } stroke={ `${ theme.colors.text.default }99` } fontSize={ 11 } />
                        <Tooltip cursor={{ fill: `${ theme.colors.text.default }0D` }} />
                        <Bar dataKey="total" name="Atendimentos" fill={ theme.colors.info.default } radius={[ 0, 6, 6, 0 ]}>
                          <LabelList dataKey="total" position="right" fill={ theme.colors.text.default } fontSize={ 12 } />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartFrame>
                )
              : <EmptyState>Nenhum atendimento no período.</EmptyState>
          }
        </Panel>

        <Panel>
          <h2>Avaliações</h2>
          {
            ratingChartData.length > 0
              ? (
                  <ChartFrame $height={ 220 }>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Tooltip />
                        <Legend />
                        <Pie data={ ratingChartData } dataKey="total" nameKey="label" innerRadius={ 54 } outerRadius={ 78 } paddingAngle={ 3 }>
                          { ratingChartData.map(( item ) => <Cell key={ item.label } fill={ item.color } />)}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </ChartFrame>
                )
              : <EmptyState>Nenhuma avaliação no período.</EmptyState>
          }
          <MetricsGrid>
            <MetricCard>
              <span>Concluídas</span>
              <strong>{ dashboard?.ratings.completed ?? 0 }</strong>
            </MetricCard>
            <MetricCard>
              <span>Sucesso</span>
              <strong>{ formatPercent( dashboard?.ratings.successRate )}</strong>
            </MetricCard>
          </MetricsGrid>
        </Panel>

      </DashboardHalfSections>

      <Panel>
        <h2>Filas</h2>
        {
          queueChartData.length > 0
            ? (
                <ChartFrame $height={ Math.max( 240, queueChartData.length * 34 ) }>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={ queueChartData } layout="vertical" margin={{ top: 8, right: 40, left: 32, bottom: 0 }}>
                      <CartesianGrid stroke={ `${ theme.colors.text.default }18` } horizontal={ false } />
                      <XAxis type="number" hide allowDecimals={ false } />
                      <YAxis type="category" dataKey="label" width={ 180 } stroke={ `${ theme.colors.text.default }99` } fontSize={ 11 } />
                      <Tooltip cursor={{ fill: `${ theme.colors.text.default }0D` }} />
                      <Bar dataKey="total" name="Atendimentos" fill={ theme.colors.success.default } radius={[ 0, 6, 6, 0 ]}>
                        <LabelList dataKey="total" position="right" fill={ theme.colors.text.default } fontSize={ 12 } />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </ChartFrame>
              )
            : <EmptyState>Nenhuma fila com dados no período.</EmptyState>
        }
      </Panel>

      <Panel>
        <h2>Classificação dos atendentes</h2>
        {
          ( dashboard?.attendants.length ?? 0 ) > 0
            ? (
                <RankingTable>
                  <div className="row header">
                    <span>Atendente</span>
                    <span>Finalizados</span>
                    <span>TMA</span>
                    <span>Classificação</span>
                  </div>
                  { dashboard?.attendants.slice( 0, 10 ).map(( attendant ) => (
                    <div className="row" key={ attendant.id }>
                      <span title={`${ attendant.email } - ${ attendant.organizationName }`}>{ attendant.name }</span>
                      <span>{ attendant.finalizedServices }</span>
                      <span>{ formatDuration( attendant.averageHandlingSeconds )}</span>
                      <span>{ attendant.classification }</span>
                    </div>
                  ))}
                </RankingTable>
              )
            : <EmptyState>Nenhum atendente classificado no período.</EmptyState>
          }
        </Panel>
              </>
            )
      }
    </DashboardView>
  );
};

async function loadCurrentServices(): Promise<Array<CurrentService>> {
  const services = await loadActiveServices();

  return services.map(( service ) => ({ service }));
}

async function loadActiveServices(): Promise<Array<Service>> {
  const perPage = 100;
  let page = 1;
  let total = 1;
  const results: Array<Service> = [];

  while( results.length < total ) {
    const response = await getServices({
      status: ServiceStatus.PROGRESS,
      order: Order.DESC,
      page,
      perPage,
    });

    if( response.data === false ) throw new Error( response.message );

    results.push( ...response.data.results );
    total = response.data.total;
    if( response.data.results.length === 0 ) break;
    page += 1;
  }

  return results;
}

function buildNowCards( services: Array<CurrentService>, mode: NowGroupMode ): Array<NowCard> {
  const cards = new Map<string, NowCard>();

  for( const current of services ) {
    const group = getNowGroup( current, mode );
    const card = cards.get( group.id ) ?? {
      ...group,
      services: [],
    };

    card.services.push( current );
    cards.set( group.id, card );
  }

  return Array.from( cards.values()).sort(( first, second ) => second.services.length - first.services.length || first.title.localeCompare( second.title ));
}

function getNowGroup( current: CurrentService, mode: NowGroupMode ): Omit<NowCard, "services"> {
  const service = current.service;

  if( mode === "users" ) {
    if( service.user?.id ) {
      return {
        id: service.user.id,
        title: service.user.name,
        subtitle: service.user.isOnline ? "Online" : "Offline",
        status: service.user.isOnline ? "online" : "offline",
        avatarName: service.user.name,
        avatarUrl: service.user.avatar,
      };
    }

    return {
      id: "without-user",
      title: "Sem atendente",
      status: "neutral",
    };
  }

  if( mode === "queues" ) {
    if( service.queue?.id ) {
      return {
        id: service.queue.id,
        title: service.queue.name,
        status: "neutral",
      };
    }

    return {
      id: "without-queue",
      title: "Sem fila",
      status: "neutral",
    };
  }

  const channel = service.chat?.channel;
  if( channel?.id ) {
    return {
      id: channel.id,
      title: channel.name || "Canal sem nome",
      subtitle: channel.status,
      status: "neutral",
    };
  }

  return {
    id: "without-channel",
    title: "Canal não identificado",
    status: "neutral",
  };
}

function renderNowCards( cards: Array<NowCard>, emptyMessage: string, icon: "user" | "queue" | "channel" ) {
  if( cards.length === 0 ) return <EmptyState>{ emptyMessage }</EmptyState>;

  return (
    <NowCardsGrid>
      { cards.map(( card ) => (
        <NowGroupCard key={ card.id }>
          <NowCardHeader $status={ card.status }>
            { icon === "user" ? <Avatar size={ 28 } name={ card.avatarName || card.title } url={ card.avatarUrl ?? null } /> : null }
            { icon === "queue" ? <ArchiveTray className="now-card-icon" size={ 24 } /> : null }
            { icon === "channel" ? <Broadcast className="now-card-icon" size={ 24 } /> : null }
            <div className="now-card-title">
              <strong>{ card.title }</strong>
              { card.subtitle ? <span>{ card.subtitle }</span> : null }
            </div>
          </NowCardHeader>

          <NowCardBody>
            {
              card.services.length > 0
                ? card.services.map(( current ) => <NowServiceItem key={ current.service.id } current={ current } />)
                : <EmptyState>Nenhum chamado aberto.</EmptyState>
            }
          </NowCardBody>

          <NowCardFooter>Total de chamados abertos: { card.services.length }</NowCardFooter>
        </NowGroupCard>
      ))}
    </NowCardsGrid>
  );
}

function NowServiceItem({ current }: { current: CurrentService }) {
  const { service } = current;
  const contact = service.chat?.contact;
  const message = service.chat?.messages?.[ 0 ];
  const contactName = contact?.name || contact?.phone || "Contato";
  const avatarURL = contact?.avatarUrl || contact?.avatar || null;
  const preview = getMessagePreview( message );
  const date = formatCurrentServiceDate( message?.createdAt ?? service.updatedAt );

  return (
    <NowServiceRow>
      <Avatar size={ 42 } name={ contactName } url={ avatarURL } />

      <div className="service-main">
        <div className="service-title">
          <strong className="service-contact-name">{ contactName }</strong>
          <span className="service-date">{ date }</span>
        </div>
        <p className="service-preview">{ preview }</p>
      </div>

      { service.unReadCount > 0 ? <span className="service-unread-badge"><Badge number={ service.unReadCount } color="error" /></span> : null }
    </NowServiceRow>
  );
}

function getMessagePreview( message?: Message | null ): string {
  if( !message ) return "Sem mensagens";
  if( message.channelDeletedAt ) return "Mensagem apagada";
  if( message.messageType === MessageType.INTERNAL ) return `Comentário interno: ${ sanitizeMessagePreview( message.content ) }`;
  if( message.content ) return sanitizeMessagePreview( message.content );

  switch( message.messageType ) {
    case MessageType.AUDIO:
      return "Enviou um áudio";
    case MessageType.IMAGE:
      return "Enviou uma imagem";
    case MessageType.STICKER:
      return "Enviou uma figurinha";
    case MessageType.VIDEO:
      return "Enviou um vídeo";
    case MessageType.DOCUMENT:
      return message.fileMetadata?.fileName || "Enviou um documento";
    case MessageType.LOCATION:
    case MessageType.LIVE_LOCATION:
      return "Enviou uma localização";
    case MessageType.CONTACT:
      return "Enviou um contato";
    case MessageType.POLL:
      return "Enviou uma enquete";
    default:
      return "Mensagem";
  }
}

function sanitizeMessagePreview( value: string ) {
  return value.replace( /[*_~`]/g, "" ).replace( /\s+/g, " " ).trim() || "Mensagem";
}

function formatCurrentServiceDate( value?: Date | string | null ) {
  const date = value ? new Date( value ) : null;
  if( !date || Number.isNaN( date.getTime())) return "";

  const now = new Date();
  const todayStart = new Date( now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const dateStart = new Date( date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const dayMs = 24 * 60 * 60 * 1000;

  if( dateStart === todayStart ) return "Hoje";
  if( dateStart === todayStart - dayMs ) return "Ontem";

  return new Intl.DateTimeFormat( "pt-BR" ).format( date );
}

function formatTime( value: Date ) {
  return new Intl.DateTimeFormat( "pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  }).format( value );
}

function formatDateInput( date: Date ) {
  return date.toISOString().slice( 0, 10 );
}

function formatDuration( seconds: number ) {
  if( seconds <= 0 ) return "0m";
  const minutes = Math.round( seconds / 60 );
  if( minutes < 60 ) return `${ minutes }m`;
  const hours = Math.floor( minutes / 60 );
  const rest = minutes % 60;
  return rest ? `${ hours }h ${ rest }m` : `${ hours }h`;
}

function formatScore( score?: number ) {
  return typeof score === "number" ? score.toFixed( 1 ) : "-";
}

function formatPercent( value?: number ) {
  return typeof value === "number" ? `${ Math.round( value ) }%` : "-";
}

function formatChartDate( value: string, showYear: boolean ) {
  const date = parseChartDate( value );
  if( !date ) return value;

  return new Intl.DateTimeFormat( "pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: showYear ? "2-digit" : undefined,
    timeZone: "UTC",
  }).format( date );
}

function getChartDateYear( value: string ) {
  return value.slice( 0, 10 ).split( "-" )[ 0 ] ?? "";
}

function getServiceQueueKey( item: DashboardDailyQueueServiceTotal ) {
  return `queue_${ item.queueId }_${ item.organizationId }`.replace( /[^a-zA-Z0-9_]/g, "_" );
}

function parseChartDate( value: string ) {
  const [ year, month, day ] = value.slice( 0, 10 ).split( "-" ).map( Number );
  if( !year || !month || !day ) return null;
  return new Date( Date.UTC( year, month - 1, day ));
}

function shouldShowDailyLabel( index: number, length: number, step: number ) {
  return index === 0 || index === length - 1 || index % step === 0;
}
